import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase, SITE_URL } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  CANCELLATION_REASONS,
  type Booking,
  type BookingEvent,
  type BookingEventKind,
  type BookingInvite,
  type Offer,
  type Vendor,
} from '../lib/database.types';
import { formatDate, formatMoney, formatTime } from '../lib/format';

type Loaded = {
  booking: Booking;
  vendor: Vendor;
  offer: Offer;
  invites: BookingInvite[];
  events: BookingEvent[];
};

// Reservations can be cancelled up until 3:00 PM NYC time on the booking
// date. The DB trigger is authoritative; this client check just gates UI.
function cancelDeadline(bookingDate: string): Date {
  const [y, m, d] = bookingDate.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 15, 0, 0);
}

// "1 minute ago" / "2 hours ago" / "3 days ago" — verbose by design so the
// activity feed reads like a chat ("Emily said yes! 1 hour ago.") rather than
// a log file (1h ago).
function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState<Loaded | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  async function reload() {
    if (!id) return;
    const [{ data: b }, { data: i }, { data: ev }] = await Promise.all([
      supabase.from('ga_bookings').select('*').eq('id', id).maybeSingle(),
      supabase.from('ga_booking_invites').select('*').eq('booking_id', id).order('created_at'),
      supabase.from('ga_booking_events').select('*').eq('booking_id', id).order('created_at', { ascending: false }),
    ]);
    if (!b) return;
    const [{ data: v }, { data: o }] = await Promise.all([
      supabase.from('ga_vendors').select('*').eq('id', b.vendor_id).maybeSingle(),
      supabase.from('ga_offers').select('*').eq('id', b.offer_id).maybeSingle(),
    ]);
    if (!v || !o) return;
    setData({ booking: b, vendor: v, offer: o, invites: i ?? [], events: ev ?? [] });
  }

  useEffect(() => { reload(); }, [id]);

  async function addInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data || !id) return;
    setInviteError(null);
    setInviteBusy(true);

    const { error } = await supabase.from('ga_booking_invites').insert({
      booking_id: id,
      email: inviteEmail.trim().toLowerCase(),
    });
    setInviteBusy(false);
    if (error) {
      setInviteError(error.message);
      return;
    }
    setInviteEmail('');
    await reload();
  }

  async function copyShareLink() {
    if (!data) return;
    await navigator.clipboard.writeText(`${SITE_URL}/bookings/${data.booking.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!data) {
    return (
      <>
        <Nav minimal />
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--ink-soft)' }}>Loading your booking…</div>
      </>
    );
  }

  const { booking, vendor, offer, invites, events } = data;
  const counts = {
    yes: invites.filter((i) => i.rsvp_status === 'yes').length,
    no: invites.filter((i) => i.rsvp_status === 'no').length,
    maybe: invites.filter((i) => i.rsvp_status === 'maybe').length,
    pending: invites.filter((i) => i.rsvp_status === 'pending').length,
  };

  const isCancelled = booking.status === 'cancelled';
  const deadline = cancelDeadline(booking.booking_date);
  const canCancel = !isCancelled && Date.now() < deadline.getTime();
  const isOrganizer = !!user && user.id === booking.organizer_user_id;

  const hype = isCancelled
    ? `Cancelled. Browse other spots when you're ready.`
    : counts.yes > 0
    ? `${counts.yes} ${counts.yes === 1 ? 'person is' : 'people are'} in. Keep the energy going.`
    : invites.length > 0
    ? `Invites are out. Keep an eye on who's locking it in.`
    : `It's official — now rally the team.`;

  return (
    <>
      <Nav minimal />
      <BookingHero
        vendor={vendor}
        booking={booking}
        offer={offer}
        isCancelled={isCancelled}
        hype={hype}
      />

      {!isCancelled && (
        <section style={{ paddingBottom: 48 }}>
          <div className="container-narrow">
            <RsvpHub
              invites={invites}
              counts={counts}
              copyShareLink={copyShareLink}
              copied={copied}
              addInvite={addInvite}
              inviteEmail={inviteEmail}
              setInviteEmail={setInviteEmail}
              inviteBusy={inviteBusy}
              inviteError={inviteError}
            />
          </div>
        </section>
      )}

      <section style={{ paddingBottom: 48 }}>
        <div className="container-narrow">
          <div className="ga-detail-grid">
            <ActivityFeed events={events} />
            <BookingFacts vendor={vendor} offer={offer} booking={booking} isCancelled={isCancelled} />
          </div>
        </div>
      </section>

      <section style={{ paddingBottom: 96 }}>
        <div className="container-narrow">
          <BookingFooterLinks
            vendor={vendor}
            bookingDate={booking.booking_date}
            isOrganizer={isOrganizer}
            isCancelled={isCancelled}
            canCancel={canCancel}
            deadline={deadline}
            onCancelClick={() => setCancelOpen(true)}
          />
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Link to="/dashboard" className="btn btn-ghost">Back to dashboard →</Link>
          </div>
        </div>
      </section>

      {cancelOpen && (
        <CancelBookingModal
          bookingId={booking.id}
          vendorName={vendor.name}
          deadline={deadline}
          onClose={() => setCancelOpen(false)}
          onCancelled={async () => {
            setCancelOpen(false);
            await reload();
          }}
        />
      )}

      <Footer />

      <style>{`
        .ga-detail-grid {
          display: grid;
          gap: 24px;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
        }
        @media (max-width: 860px) {
          .ga-detail-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </>
  );
}

function BookingHero({
  vendor,
  booking,
  offer,
  isCancelled,
  hype,
}: {
  vendor: Vendor;
  booking: Booking;
  offer: Offer;
  isCancelled: boolean;
  hype: string;
}) {
  const bg = vendor.hero_image_url
    ? `linear-gradient(180deg, rgba(20,16,14,0.15) 30%, rgba(20,16,14,0.78) 100%), url(${vendor.hero_image_url}) center/cover no-repeat`
    : `linear-gradient(135deg, oklch(0.32 0.10 ${(vendor.name.charCodeAt(0) * 7) % 360}), oklch(0.18 0.10 ${((vendor.name.charCodeAt(0) * 7) + 30) % 360}))`;

  return (
    <section
      style={{
        position: 'relative',
        marginTop: 16,
        padding: '60px 0 64px',
        color: '#fff',
        background: bg,
      }}
    >
      <div className="container-narrow">
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.85 }}>
          {isCancelled ? 'Cancelled' : 'Booked'} · {offer.title}
        </div>
        <h1
          className="display"
          style={{
            fontSize: 'clamp(40px, 6vw, 72px)',
            margin: '12px 0 14px',
            lineHeight: 1.05,
            color: '#fff',
          }}
        >
          {vendor.name},{' '}
          <span className="serif-italic" style={{ color: 'oklch(0.85 0.10 60)' }}>
            {formatDate(booking.booking_date).toLowerCase()}
          </span>
          .
        </h1>
        <p style={{ fontSize: 18, opacity: 0.92, margin: 0, maxWidth: 620 }}>
          {hype}
        </p>
        <div
          style={{
            marginTop: 22,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <HeroPill label="When" value={`${formatDate(booking.booking_date)} · ${formatTime(booking.booking_time)}`} />
          <HeroPill label="Who" value={`${booking.headcount} ${booking.headcount === 1 ? 'person' : 'people'}`} />
          <HeroPill label="Where" value={vendor.neighborhood ?? 'NYC'} />
          {!isCancelled && <HeroPill label="Total" value={formatMoney(booking.total_cents)} />}
        </div>
      </div>
    </section>
  );
}

function HeroPill({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.2)',
        padding: '8px 14px',
        borderRadius: 999,
        fontSize: 14,
        color: '#fff',
        display: 'inline-flex',
        gap: 8,
        alignItems: 'baseline',
      }}
    >
      <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.75 }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function RsvpHub({
  invites,
  counts,
  copyShareLink,
  copied,
  addInvite,
  inviteEmail,
  setInviteEmail,
  inviteBusy,
  inviteError,
}: {
  invites: BookingInvite[];
  counts: { yes: number; maybe: number; no: number; pending: number };
  copyShareLink: () => void;
  copied: boolean;
  addInvite: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteBusy: boolean;
  inviteError: string | null;
}) {
  return (
    <div style={{
      marginTop: 36,
      padding: 32,
      borderRadius: 22,
      background: 'var(--card)',
      border: '1px solid var(--line)',
    }}>
      <div className="row-between" style={{ marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 6 }}>
            The team · {invites.length} {invites.length === 1 ? 'invite' : 'invites'}
          </div>
          <h2 className="display" style={{ fontSize: 28, margin: 0 }}>Who's in?</h2>
        </div>
        <button onClick={copyShareLink} className="btn btn-primary btn-sm">
          {copied ? 'Copied!' : (
            <>
              <IconLink /> Copy share link
            </>
          )}
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
        marginBottom: 20,
      }}>
        <StatTile n={counts.yes} label="Yes" tone="go" />
        <StatTile n={counts.maybe} label="Maybe" tone="warm" />
        <StatTile n={counts.no} label="No" tone="quiet" />
        <StatTile n={counts.pending} label="Pending" tone="quiet" />
      </div>

      {invites.length > 0 && (
        <div className="stack-sm" style={{ marginBottom: 20 }}>
          {invites.map((i) => (
            <div key={i.id} className="row-between" style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{i.name || i.email}</div>
                {i.name && <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{i.email}</div>}
              </div>
              <span className={`tag ${i.rsvp_status === 'yes' ? 'tag-active' : i.rsvp_status === 'no' ? 'tag-cancelled' : 'tag-pending'}`}>
                {i.rsvp_status}
              </span>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={addInvite} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
        <input
          type="email"
          placeholder="teammate@yourcompany.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          required
        />
        <button className="btn btn-primary" disabled={inviteBusy}>{inviteBusy ? '…' : 'Invite'}</button>
      </form>
      {inviteError && <div className="field-error">{inviteError}</div>}
      <p className="field-hint" style={{ marginTop: 12 }}>
        Each invitee gets a one-click RSVP link — no login required.
      </p>
    </div>
  );
}

function ActivityFeed({ events }: { events: BookingEvent[] }) {
  // We re-derive these on every render so the relative timestamps stay fresh
  // without a tick / interval. The page reloads on visit, which is enough.
  const items = useMemo(() => events.map((e) => describe(e)), [events]);
  return (
    <div style={{
      padding: 28,
      borderRadius: 18,
      background: 'var(--card)',
      border: '1px solid var(--line)',
    }}>
      <div className="row-between" style={{ marginBottom: 18 }}>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
          Activity
        </div>
        <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
          {events.length} {events.length === 1 ? 'event' : 'events'}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>Nothing yet. As people RSVP, you'll see them pop in here.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map((item) => (
            <div key={item.id} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div
                aria-hidden
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: item.bg,
                  color: item.fg,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 17,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, color: 'var(--ink)' }}>{item.text}</div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
                  {timeAgo(item.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function describe(e: BookingEvent) {
  const who = e.actor_name || e.actor_email || 'Someone';
  const friendly = who.includes('@') ? who.split('@')[0] : who;
  const map: Record<BookingEventKind, { icon: string; bg: string; fg: string; text: React.ReactNode }> = {
    booking_created: {
      icon: '✨',
      bg: 'oklch(0.94 0.06 70)',
      fg: 'oklch(0.42 0.14 70)',
      text: <><strong>{friendly}</strong> booked the round.</>,
    },
    invite_added: {
      icon: '📨',
      bg: 'oklch(0.94 0.04 240)',
      fg: 'oklch(0.42 0.10 240)',
      text: <><strong>{friendly}</strong> was invited.</>,
    },
    rsvp_yes: {
      icon: '🎉',
      bg: 'oklch(0.92 0.08 145)',
      fg: 'oklch(0.32 0.12 145)',
      text: <><strong>{friendly}</strong> said yes!</>,
    },
    rsvp_no: {
      icon: '✗',
      bg: 'var(--bg)',
      fg: 'var(--ink-soft)',
      text: <><strong>{friendly}</strong> can't make it.</>,
    },
    rsvp_maybe: {
      icon: '🤔',
      bg: 'oklch(0.94 0.06 70)',
      fg: 'oklch(0.42 0.14 70)',
      text: <><strong>{friendly}</strong> is a maybe.</>,
    },
    booking_cancelled: {
      icon: '✕',
      bg: 'oklch(0.94 0.06 25)',
      fg: 'oklch(0.42 0.14 25)',
      text: <><strong>{friendly}</strong> cancelled the booking.</>,
    },
  };
  const m = map[e.kind] ?? {
    icon: '•',
    bg: 'var(--bg)',
    fg: 'var(--ink-soft)',
    text: <><strong>{friendly}</strong> · {e.kind}</>,
  };
  return { id: e.id, created_at: e.created_at, ...m };
}

function BookingFacts({
  vendor,
  offer,
  booking,
  isCancelled,
}: {
  vendor: Vendor;
  offer: Offer;
  booking: Booking;
  isCancelled: boolean;
}) {
  return (
    <div style={{ padding: 28, borderRadius: 18, background: 'var(--card)', border: '1px solid var(--line)' }}>
      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 14 }}>
        Details
      </div>
      <Row k="Bar" v={vendor.name} />
      <Row k="Address" v={vendor.address ?? '-'} />
      <Row k="Package" v={offer.title} />
      <Row k="Date" v={`${formatDate(booking.booking_date)} · ${formatTime(booking.booking_time)}`} />
      <Row k="Headcount" v={String(booking.headcount)} />
      <Row k="Total" v={formatMoney(booking.total_cents)} />
      <Row k="Status" v={<span className={`tag ${isCancelled ? 'tag-cancelled' : 'tag-active'}`}>{booking.status}</span>} />
      {isCancelled && booking.cancellation_date && (
        <Row k="Cancelled" v={new Date(booking.cancellation_date).toLocaleString()} />
      )}
    </div>
  );
}

function StatTile({ n, label, tone }: { n: number; label: string; tone: 'go' | 'warm' | 'quiet' }) {
  const bg =
    tone === 'go' ? 'oklch(0.92 0.08 145)' :
    tone === 'warm' ? 'oklch(0.94 0.06 70)' :
    'var(--bg)';
  const color =
    tone === 'go' ? 'oklch(0.32 0.12 145)' :
    tone === 'warm' ? 'oklch(0.42 0.14 70)' :
    'var(--ink)';
  return (
    <div style={{
      background: bg,
      borderRadius: 14,
      padding: '14px 10px',
      textAlign: 'center',
      border: tone === 'quiet' ? '1px solid var(--line)' : 'none',
    }}>
      <div className="display" style={{ fontSize: 28, color, lineHeight: 1 }}>{n}</div>
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 6 }}>
        {label}
      </div>
    </div>
  );
}

function BookingFooterLinks({
  vendor,
  bookingDate,
  isOrganizer,
  isCancelled,
  canCancel,
  deadline,
  onCancelClick,
}: {
  vendor: Vendor;
  bookingDate: string;
  isOrganizer: boolean;
  isCancelled: boolean;
  canCancel: boolean;
  deadline: Date;
  onCancelClick: () => void;
}) {
  const cutoffLabel = deadline.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const contactHref = vendor.contact_email
    ? `mailto:${vendor.contact_email}`
    : vendor.contact_phone
    ? `tel:${vendor.contact_phone.replace(/[^+\d]/g, '')}`
    : null;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 18,
      padding: '20px 0',
      borderTop: '1px solid var(--line)',
      borderBottom: '1px solid var(--line)',
    }}>
      {!isCancelled && (
        isOrganizer && canCancel ? (
          <button
            type="button"
            onClick={onCancelClick}
            title={`Cancel until ${cutoffLabel}`}
            style={linkStyle}
          >
            <IconX /> Cancel booking
          </button>
        ) : !isOrganizer ? null : (
          <span style={{ ...linkStyle, color: 'var(--ink-soft)', cursor: 'not-allowed' }}>
            <IconX /> Cancellation window closed
          </span>
        )
      )}

      {contactHref ? (
        <a href={contactHref} style={linkStyle}>
          <IconMail /> Contact {vendor.name}
        </a>
      ) : (
        <span style={{ ...linkStyle, color: 'var(--ink-soft)' }}>
          <IconMail /> Contact info coming soon
        </span>
      )}

      {!isCancelled && isOrganizer && canCancel && (
        <div style={{ width: '100%', textAlign: 'center', fontSize: 13, color: 'var(--ink-soft)' }}>
          Need to change plans? You can cancel free until <strong style={{ color: 'var(--ink)' }}>3:00 PM</strong> on {formatDate(bookingDate)}.
        </div>
      )}
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: 'transparent',
  border: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: 15,
  color: 'var(--ink)',
  textDecoration: 'underline',
  textUnderlineOffset: 4,
  cursor: 'pointer',
};

function CancelBookingModal({
  bookingId,
  vendorName,
  deadline,
  onClose,
  onCancelled,
}: {
  bookingId: string;
  vendorName: string;
  deadline: Date;
  onClose: () => void;
  onCancelled: () => Promise<void>;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cutoffLabel = useMemo(() => deadline.toLocaleString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }), [deadline]);

  const canSubmit = (selected.length > 0 || (otherChecked && otherText.trim().length > 0));

  function toggle(reason: string) {
    setSelected((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason],
    );
  }

  async function submit() {
    setBusy(true);
    setError(null);
    const reasons = otherChecked && otherText.trim() ? [...selected, 'Other'] : selected;
    const { error: err } = await supabase
      .from('ga_bookings')
      .update({
        status: 'cancelled',
        cancellation_reasons: reasons,
        cancellation_other_reason: otherChecked && otherText.trim() ? otherText.trim() : null,
      })
      .eq('id', bookingId);
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    await onCancelled();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 16, 14, 0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          borderRadius: 20,
          maxWidth: 520,
          width: '100%',
          padding: 28,
          maxHeight: '92vh',
          overflowY: 'auto',
          border: '1px solid var(--line)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.35)',
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 14 }}>Cancel booking</div>
        <h2 className="display" style={{ fontSize: 28, margin: '0 0 10px' }}>
          Cancel your round at <span className="serif-italic" style={{ color: 'var(--wine)' }}>{vendorName}</span>?
        </h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Free cancellation through <strong style={{ color: 'var(--ink)' }}>{cutoffLabel}</strong>. Tell us why so we can do better.
        </p>

        <div className="field-label" style={{ marginTop: 18 }}>Reason (select any)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {CANCELLATION_REASONS.map((r) => {
            const on = selected.includes(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() => toggle(r)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: `1.5px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                  background: on ? 'var(--ink)' : 'transparent',
                  color: on ? 'var(--bg)' : 'var(--ink)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                {r}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setOtherChecked((v) => !v)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: `1.5px solid ${otherChecked ? 'var(--ink)' : 'var(--line)'}`,
              background: otherChecked ? 'var(--ink)' : 'transparent',
              color: otherChecked ? 'var(--bg)' : 'var(--ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Other
          </button>
        </div>

        {otherChecked && (
          <textarea
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Tell us more…"
            rows={3}
            style={{ marginTop: 12, resize: 'vertical' }}
          />
        )}

        {error && <div className="field-error" style={{ marginTop: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn btn-ghost" disabled={busy}>Keep booking</button>
          <button
            type="button"
            onClick={submit}
            className="btn btn-danger"
            disabled={!canSubmit || busy}
          >
            {busy ? 'Cancelling…' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="row-between" style={{ padding: '8px 0' }}>
      <span className="muted">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function IconX() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}
